import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Alert,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { FaFileDownload, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';

interface DateRangePickerProps {
  onExport: (startDate: Date, endDate: Date) => void;
  maxMonths?: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ onExport, maxMonths = 12 }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Validate the date range
    validateDateRange(startDate, endDate);
  }, [startDate, endDate]);

  const validateDateRange = (start: Dayjs, end: Dayjs): boolean => {
    if (!start || !end) {
      setError('Les deux dates sont requises');
      return false;
    }
    
    if (end.isBefore(start)) {
      setError('La date de fin doit être après la date de début');
      return false;
    }
    
    const diffMonths = end.diff(start, 'month', true);
    if (diffMonths > maxMonths) {
      setError(`La période ne peut pas dépasser ${maxMonths} mois`);
      return false;
    }
    
    setError('');
    return true;
  };

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleExport = (): void => {
    if (validateDateRange(startDate, endDate)) {
      onExport(startDate.toDate(), endDate.toDate());
      handleClose();
    }
  };

  type PeriodType = 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'lastQuarter' | 'thisYear';

  const setPresetPeriod = (period: PeriodType): void => {
    switch (period) {
      case 'last7days':
        setStartDate(dayjs().subtract(7, 'day'));
        setEndDate(dayjs());
        break;
      case 'last30days':
        setStartDate(dayjs().subtract(30, 'day'));
        setEndDate(dayjs());
        break;
      case 'thisMonth':
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
        break;
      case 'lastMonth':
        setStartDate(dayjs().subtract(1, 'month').startOf('month'));
        setEndDate(dayjs().subtract(1, 'month').endOf('month'));
        break;
      case 'lastQuarter':
        setStartDate(dayjs().subtract(3, 'month').startOf('month'));
        setEndDate(dayjs());
        break;
      case 'thisYear':
        setStartDate(dayjs().startOf('year'));
        setEndDate(dayjs());
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<FaFileDownload />}
        onClick={handleOpen}
      >
        Exporter les données
      </Button>
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FaCalendarAlt style={{ marginRight: '8px' }} />
            Sélectionner la période d'export
            <Tooltip title="Maximum 12 mois d'historique">
              <IconButton size="small" sx={{ ml: 1 }}>
                <FaInfoCircle />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sélectionnez une période (maximum 12 mois)
            </Typography>
            
            <Box display="flex" flexWrap="wrap" gap={1} mb={3} mt={2}>
              <Button size="small" variant="outlined" onClick={() => setPresetPeriod('last7days')}>
                7 derniers jours
              </Button>
              <Button size="small" variant="outlined" onClick={() => setPresetPeriod('last30days')}>
                30 derniers jours
              </Button>
              <Button size="small" variant="outlined" onClick={() => setPresetPeriod('thisMonth')}>
                Mois en cours
              </Button>
              <Button size="small" variant="outlined" onClick={() => setPresetPeriod('lastMonth')}>
                Mois précédent
              </Button>
              <Button size="small" variant="outlined" onClick={() => setPresetPeriod('lastQuarter')}>
                Dernier trimestre
              </Button>
              <Button size="small" variant="outlined" onClick={() => setPresetPeriod('thisYear')}>
                Année en cours
              </Button>
            </Box>
            
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
              <Box display="flex" flexDirection="column" gap={3}>
                <DatePicker
                  label="Date de début"
                  value={startDate}
                  onChange={(newValue: Dayjs | null) => newValue && setStartDate(newValue)}
                  maxDate={dayjs()}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label="Date de fin"
                  value={endDate}
                  onChange={(newValue: Dayjs | null) => newValue && setEndDate(newValue)}
                  maxDate={dayjs()}
                  minDate={startDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            
            {!error && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Période sélectionnée: {startDate.format('DD/MM/YYYY')} - {endDate.format('DD/MM/YYYY')}
                <br />
                Durée: {Math.round(endDate.diff(startDate, 'day', true))} jours
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button 
            onClick={handleExport} 
            variant="contained" 
            color="primary"
            disabled={!!error}
            startIcon={<FaFileDownload />}
          >
            Exporter
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DateRangePicker;